import { array, boolean, number, object, string, StringSchema } from 'yup';

const HEX_CODE_REGEX = /^#([a-f0-9]{8}|[a-f0-9]{6}|[a-f0-9]{3})$/i;
const URL_REGEX = /^https?:\/\/[(www\\.)?a-zA-Z0-9@:%._\\+~#=-]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/i;

const HEX_CODE_ERROR = 'Color codes must begin with # and be 3-8 characters long';
const URL_ERROR = 'URLs must have a valid domain and protocol prefix';

const baseNavLinkSchema = object().shape({
	label: string().when('destination', {
		is: (destination: string) => destination,
		then: (schema: StringSchema) => schema.required('Links must have a text label'),
	}),
	destination: string()
		.matches(URL_REGEX, { excludeEmptyString: true, message: URL_ERROR })
		.when('children', {
			is: (children: object[]) => children?.length > 0,
			then: (schema: StringSchema) => schema.max(0, 'Menu headers must not have a destination'),
		}),
});

export const navLinkSchema = baseNavLinkSchema.shape({
	children: array().of(baseNavLinkSchema).nullable(),
});

const blogFontSchema = object().shape({
	fontName: string(),
	fontUri: string().matches(URL_REGEX, { excludeEmptyString: true, message: URL_ERROR }),
});

const textAttributesSchema = object().shape({
	font: blogFontSchema,
	color: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	backgroundColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
});

export const headerSchema = object().shape({
	backgroundColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	dropdown: textAttributesSchema,
	hoverColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	menuLinks: array().of(navLinkSchema),
	overallFont: blogFontSchema,
	textColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	mainPageTitle: string().max(100, 'Page title must be 100 characters or less'),
});

export const footerSchema = object().shape({
	backgroundColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	textColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	linkColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	text: object().shape({
		document: string(),
		documentVersion: number(),
		source: string(),
	}),
	copyrightInfo: string().nullable(),
	links: array().of(navLinkSchema),
});

const customTitleTextSchema = textAttributesSchema.shape({
	title: string(),
});

const pageButtonSchema = textAttributesSchema.shape({
	hoverColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
});

const blogCardSchema = object().shape({
	titleStyle: textAttributesSchema,
	linkStyle: textAttributesSchema,
	showAuthorMetaData: boolean(),
});

export const blogHomeSchema = object().shape({
	pageBackgroundColor: string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR }),
	headerImage: object(),
	hideNook: boolean(),
	font: blogFontSchema,
	blogTitle: customTitleTextSchema,
	cardStyle: blogCardSchema,
});

export const blogPostSchema = object().shape({
	blogTitle: textAttributesSchema,
	blogHeadlines: textAttributesSchema,
	blogBody: textAttributesSchema,
	showMoreButton: pageButtonSchema,
});
